import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useCatalogDialog } from "@/hooks/use-catalog-dialog";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";
import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useTranslation } from "react-i18next";
import { DirtyIndicator } from "@/components/ui/dirty-indicator";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";
import { getCurrentUser } from "@/lib/api/auth";

import { AdaptiveSheet } from "@/components/ui/adaptive-sheet";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, Text } from "@/components/ui/text";
import { Fab } from "@/components/ui/fab";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { FormLabel } from "@/components/ui/form-label";
import { Icon } from "@/components/ui/icons";
import { InputWrapper } from "@/components/ui/input-wrapper";
import { PageLoadingOverlay } from "@/components/ui/page-loading-overlay";
import { ModalBody, ModalFooter, ModalHeader } from "@/components/ui/modal";
import { PageTitle } from "@/contexts/page-title-context";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { ResizeHandle } from "@/components/ui/resize-handle";
import {
  ResizableTableShell,
  type ColumnDef,
} from "@/components/ui/resizable-table-shell";
import { FormGrid } from "@/components/ui/form-grid";
import { HStack } from "@/components/ui/h-stack";
import { TableViewport } from "@/components/ui/table-viewport";
import { VStack } from "@/components/ui/v-stack";
import { useSizeClass } from "@/hooks/use-size-class";
import { TableSkeleton } from "@/components/admin/table-skeleton";
import { DeletedIndicatorCell } from "@/components/admin/catalogs/deleted-indicator-cell";
import { ConfirmActionButton } from "@/components/admin/catalogs/confirm-action-button";
import { DeletedWarningBanner } from "@/components/admin/catalogs/deleted-warning-banner";
import { useDeleteRestore } from "@/hooks/useDeleteRestore";
import type { Ref } from "@/lib/api/types";
import type { TruckModel } from "@/lib/api/trucks";
import { getTruckManufacturers, getTruckModels } from "@/lib/api/trucks";
import {
  createTruckManufacturer,
  createTruckModel,
  updateTruckManufacturer,
  updateTruckModel,
} from "@/lib/api/catalogs";
import { selectionClassName } from "@/lib/selection";
import { useForm } from "@/hooks/useForm";
import { z } from "zod";
import { useGuardedClose } from "@/hooks/useGuardedClose";

type ManufacturerRow = Ref & { is_deleted?: boolean };
type TruckModelRow = TruckModel & { is_deleted?: boolean };

type ModalKind = "manufacturer" | "model";

function AdminTruckCatalogPage() {
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const { t: tAdminNav } = useTranslation("adminNav");
  const { t: tCatalogTruckManufacturers } = useTranslation(
    "admin.catalogs.truckManufacturers",
  );
  const { t: tCatalogTruckModels } = useTranslation(
    "admin.catalogs.truckModels",
  );
  const { t: tCommon } = useTranslation("common");

  const queryClient = useQueryClient();
  const { offset } = usePageOffset();
  const {
    data: manufacturersData,
    isLoading: manufacturersLoading,
    isError: loadError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.admin.truckMakes({ limit: DEFAULT_PAGE_SIZE, offset }),
    queryFn: () => getTruckManufacturers({ limit: DEFAULT_PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });
  const { data: modelsData, isLoading: modelsLoading } = useQuery({
    queryKey: ["admin", "catalogs", "truck-makes", "all-models"],
    queryFn: () => getTruckModels({ limit: 200 }),
    staleTime: 60_000,
  });
  const loading = manufacturersLoading || modelsLoading;
  const manufacturers = useMemo(
    () => (manufacturersData?.data ?? []) as ManufacturerRow[],
    [manufacturersData?.data],
  );
  const total = manufacturersData?.total ?? 0;
  const models = useMemo(
    () => (modelsData?.data ?? []) as TruckModelRow[],
    [modelsData?.data],
  );
  const { page, totalPages, setPage } = usePagination({ total });
  const [saveError, setSaveError] = useState(false);

  const { filters: searchFilters, setFilter: setSearchFilter } =
    useFilterSearchParams({ keys: ["mfgSearch", "modelSearch"] });
  const manufacturerQuery = searchFilters.mfgSearch;
  const setManufacturerQuery = (v: string) => setSearchFilter("mfgSearch", v);
  const modelQuery = searchFilters.modelSearch;
  const setModelQuery = (v: string) => setSearchFilter("modelSearch", v);

  const [selectedManufacturerId, setSelectedManufacturerId] =
    useState<string>("");

  const mfgDialog = useCatalogDialog<ManufacturerRow>();
  const modelDialog = useCatalogDialog<TruckModelRow>();
  const modalOpen = mfgDialog.isOpen || modelDialog.isOpen;
  const modalKind: ModalKind = mfgDialog.isOpen ? "manufacturer" : "model";
  const editingManufacturer = mfgDialog.selectedItem;
  const editingModel = modelDialog.selectedItem;
  const [submitting, setSubmitting] = useState(false);
  const nameSchema = z.object({ name: z.string().min(1) });
  const manufacturerForm = useForm({
    schema: nameSchema,
    defaults: { name: "" },
    onSubmit: async () => {},
  });
  const modelForm = useForm({
    schema: nameSchema,
    defaults: { name: "" },
    onSubmit: async () => {},
  });
  const isModalDirty =
    modalKind === "manufacturer" ? manufacturerForm.isDirty : modelForm.isDirty;
  const closeModal = () => {
    mfgDialog.close();
    modelDialog.close();
  };
  const { handleClose, confirmDialog: closeConfirmDialog } = useGuardedClose(
    isModalDirty,
    closeModal,
  );

  const {
    loading: deleteManufacturerLoading,
    toggle: toggleDeleteManufacturer,
  } = useDeleteRestore<ManufacturerRow>({
    endpoint: "/truck-manufacturers",
    onSuccess: (_isDeleted, data) => {
      mfgDialog.openEdit(data as ManufacturerRow);
      void queryClient.invalidateQueries({
        queryKey: ["admin", "catalogs", "truck-makes"],
      });
    },
  });

  const { loading: deleteModelLoading, toggle: toggleDeleteModel } =
    useDeleteRestore<TruckModelRow>({
      endpoint: "/truck-models",
      onSuccess: (_isDeleted, data) => {
        modelDialog.openEdit(data as TruckModelRow);
        void queryClient.invalidateQueries({
          queryKey: ["admin", "catalogs", "truck-makes"],
        });
      },
    });

  useEffect(() => {
    if (!selectedManufacturerId && manufacturers.length > 0) {
      setSelectedManufacturerId(manufacturers[0].id);
    }
  }, [manufacturers, selectedManufacturerId]);

  const filteredManufacturers = useMemo(() => {
    const q = manufacturerQuery.trim().toLowerCase();
    if (!q) return manufacturers;
    return manufacturers.filter((m) => m.name.toLowerCase().includes(q));
  }, [manufacturerQuery, manufacturers]);

  const filteredModels = useMemo(() => {
    const q = modelQuery.trim().toLowerCase();
    if (!q) return models;
    return models.filter((m) => m.name.toLowerCase().includes(q));
  }, [modelQuery, models]);

  const selectedManufacturer = useMemo(() => {
    return manufacturers.find((m) => m.id === selectedManufacturerId) ?? null;
  }, [manufacturers, selectedManufacturerId]);

  const selectedManufacturerModels = useMemo(() => {
    if (!selectedManufacturerId) return [];
    return filteredModels.filter(
      (m) => m.manufacturer?.id === selectedManufacturerId,
    );
  }, [filteredModels, selectedManufacturerId]);

  function openCreateManufacturer() {
    modelDialog.close();
    mfgDialog.openCreate();
    manufacturerForm.reset({ name: "" });
  }

  function openEditManufacturer(item: ManufacturerRow) {
    modelDialog.close();
    mfgDialog.openEdit(item);
    manufacturerForm.reset({ name: item.name });
  }

  function openCreateModel() {
    mfgDialog.close();
    modelDialog.openCreate();
    modelForm.reset({ name: "" });
  }

  function openEditModel(item: TruckModelRow) {
    mfgDialog.close();
    modelDialog.openEdit(item);
    modelForm.reset({ name: item.name });
  }

  const isModalValid = useMemo(() => {
    if (modalKind === "manufacturer") {
      if (!manufacturerForm.fields.name.trim()) return false;
      if (editingManufacturer) return manufacturerForm.isDirty;
      return true;
    }

    if (!modelForm.fields.name.trim() || !selectedManufacturerId) return false;
    if (editingModel) return modelForm.isDirty;
    return true;
  }, [
    editingManufacturer,
    editingModel,
    manufacturerForm.fields.name,
    manufacturerForm.isDirty,
    modalKind,
    modelForm.fields.name,
    modelForm.isDirty,
    selectedManufacturerId,
  ]);

  async function handleSave() {
    try {
      setSubmitting(true);
      setSaveError(false);

      if (modalKind === "manufacturer") {
        if (editingManufacturer) {
          await updateTruckManufacturer(editingManufacturer.id, {
            name: manufacturerForm.fields.name,
          });
        } else {
          const created = (await createTruckManufacturer({
            name: manufacturerForm.fields.name,
          })) as unknown as ManufacturerRow;
          setSelectedManufacturerId(created.id);
        }
        void queryClient.invalidateQueries({
          queryKey: ["admin", "catalogs", "truck-makes"],
        });
        manufacturerForm.reset();
      } else {
        if (!selectedManufacturerId) return;
        if (editingModel) {
          await updateTruckModel(editingModel.id, {
            name: modelForm.fields.name,
          });
        } else {
          await createTruckModel({
            name: modelForm.fields.name,
            manufacturer_id: selectedManufacturerId,
          });
        }
        void queryClient.invalidateQueries({
          queryKey: ["admin", "catalogs", "truck-makes"],
        });
        modelForm.reset();
      }

      closeModal();
    } catch (error) {
      console.error("Failed to save truck catalog item", error);
      setSaveError(true);
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Resizable columns (models table) ─────────────────────────────
  const userId = getCurrentUser()?.id ?? null;
  const modelColumnDefs: ColumnDef[] = [
    { key: "deleted", defaultWidth: 40, minWidth: 40 },
    { key: "name", defaultWidth: 200, minWidth: 100 },
    { key: "actions", defaultWidth: 120, minWidth: 80 },
  ];

  return (
    <VStack gap="lg" data-testid="admin-catalog-truck-catalog-page">
      <PageTitle>{tAdminNav("truckCatalog")}</PageTitle>

      {loadError || saveError ? (
        <ErrorState
          title={
            loadError ? tCommon("errorLoadingData") : tCommon("errorOccurred")
          }
          action={
            loadError ? (
              <Button
                color="neutral"
                variant="outline"
                onClick={() => void refetch()}
              >
                {tCommon("retry")}
              </Button>
            ) : undefined
          }
          className="py-10"
        />
      ) : null}

      <FormGrid cols="lg" gap="xl">
        <VStack gap="lg">
          <Card variant="bordered" padding="xs">
            <FilterToolbar
              searchPlaceholder={tCatalogTruckManufacturers(
                "searchPlaceholder",
              )}
              searchValue={manufacturerQuery}
              onSearchChange={setManufacturerQuery}
              createAction={{
                label: tCatalogTruckManufacturers("add"),
                onClick: openCreateManufacturer,
              }}
              pagination={{
                page,
                totalPages,
                perPage: DEFAULT_PAGE_SIZE,
                onPageChange: setPage,
                loading,
              }}
              data-testid="catalog-truck-catalog-manufacturers-toolbar"
            />
          </Card>

          <Card variant="bordered" padding="none">
            {loading && manufacturers.length === 0 ? (
              <VStack gap="none" padding="md">
                <TableSkeleton
                  rows={4}
                  headers={[
                    "",
                    tCatalogTruckManufacturers("title"),
                    tCommon("actions"),
                  ]}
                />
              </VStack>
            ) : filteredManufacturers.length === 0 ? (
              <VStack gap="none" className="px-4">
                <EmptyState
                  icon={<Icon.Search size="xl" />}
                  title={tCatalogTruckManufacturers("noItems")}
                  className="py-10"
                />
              </VStack>
            ) : (
              <TableViewport position="relative">
                <Table>
                  <TableBody>
                    {filteredManufacturers.map((m) => {
                      const isSelected = m.id === selectedManufacturerId;
                      const isDeleted = Boolean(m.is_deleted);
                      const mutedClass = isDeleted
                        ? "text-[color:var(--on-surface-variant)] opacity-70"
                        : "";
                      return (
                        <TableRow
                          key={m.id}
                          onClick={() => setSelectedManufacturerId(m.id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedManufacturerId(m.id);
                            }
                          }}
                          clickable
                          hoverable={!isSelected}
                          className={selectionClassName(isSelected, "row")}
                        >
                          <TableCell className="w-10">
                            <DeletedIndicatorCell isDeleted={isDeleted} />
                          </TableCell>
                          <TableCell className="w-full">
                            <Text
                              color="default"
                              weight="medium"
                              className={mutedClass}
                            >
                              {m.name}
                            </Text>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              type="button"
                              color="neutral"
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditManufacturer(m);
                              }}
                            >
                              {tCatalogTruckManufacturers("edit")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {loading && manufacturers.length > 0 && (
                  <PageLoadingOverlay
                    page={page}
                    label={tCommon("pagination.loadingPage")}
                  />
                )}
              </TableViewport>
            )}
          </Card>
        </VStack>

        <VStack gap="lg">
          <Card variant="bordered" padding="xs">
            <FilterToolbar
              searchPlaceholder={tCatalogTruckModels("searchPlaceholder")}
              searchValue={modelQuery}
              onSearchChange={setModelQuery}
              createAction={{
                label: tCatalogTruckModels("add"),
                onClick: openCreateModel,
              }}
              data-testid="catalog-truck-catalog-models-toolbar"
            />
          </Card>

          <Card variant="bordered" padding="none">
            {loading ? (
              <VStack gap="none" padding="lg">
                <TableSkeleton
                  headers={[
                    "",
                    tCatalogTruckModels("columns.name"),
                    tCatalogTruckModels("columns.actions"),
                  ]}
                />
              </VStack>
            ) : !selectedManufacturerId ? (
              <EmptyState
                icon={<Icon.ArrowLeft size="xl" />}
                title={tCatalogTruckManufacturers("noItems")}
                className="py-10"
              />
            ) : selectedManufacturerModels.length === 0 ? (
              <EmptyState
                icon={<Icon.Search size="xl" />}
                title={tCatalogTruckModels("noItems")}
                className="py-10"
              />
            ) : (
              <ResizableTableShell
                tableId="catalog-truck-catalog-model"
                userId={userId}
                columns={modelColumnDefs}
              >
                {({ onResizeStart: onModelResizeStart }) => (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell className="w-10" />
                        <TableHeaderCell className="relative">
                          {tCatalogTruckModels("columns.name")}
                          <ResizeHandle
                            columnKey="name"
                            onResizeStart={onModelResizeStart}
                          />
                        </TableHeaderCell>
                        <TableHeaderCell className="relative text-right" sticky>
                          {tCatalogTruckModels("columns.actions")}
                          <ResizeHandle
                            columnKey="actions"
                            onResizeStart={onModelResizeStart}
                          />
                        </TableHeaderCell>
                        <TableHeaderCell className="w-auto" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedManufacturerModels.map((m) => {
                        const isDeleted = Boolean(m.is_deleted);
                        const mutedClass = isDeleted
                          ? "text-[color:var(--on-surface-variant)] opacity-70"
                          : "";
                        return (
                          <TableRow key={m.id}>
                            <TableCell className="w-10">
                              <DeletedIndicatorCell isDeleted={isDeleted} />
                            </TableCell>
                            <TableCell className={`font-medium ${mutedClass}`}>
                              {m.name}
                            </TableCell>
                            <TableCell className="text-right" sticky>
                              <Button
                                type="button"
                                color="neutral"
                                variant="outline"
                                size="sm"
                                onClick={() => openEditModel(m)}
                              >
                                {tCatalogTruckModels("edit")}
                              </Button>
                            </TableCell>
                            <TableCell />
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </>
                )}
              </ResizableTableShell>
            )}
          </Card>
        </VStack>
      </FormGrid>

      {isCompact && (
        <Fab
          onClick={
            selectedManufacturerId ? openCreateModel : openCreateManufacturer
          }
          aria-label={
            selectedManufacturerId
              ? tCatalogTruckModels("add")
              : tCatalogTruckManufacturers("add")
          }
          data-testid="catalog-truck-catalog-fab"
        />
      )}

      <AdaptiveSheet
        open={modalOpen}
        onDismiss={handleClose}
        onSubmit={() => void handleSave()}
        size="md"
        data-testid="admin-truck-catalog-modal"
      >
        <ModalHeader>
          <Text weight="medium">
            {modalKind === "manufacturer"
              ? editingManufacturer
                ? tCatalogTruckManufacturers("modalEditTitle")
                : tCatalogTruckManufacturers("modalAddTitle")
              : editingModel
                ? tCatalogTruckModels("modalEditTitle")
                : tCatalogTruckModels("modalAddTitle")}
            {isModalDirty ? <DirtyIndicator /> : null}
          </Text>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={submitting}
          >
            <Icon.X size="sm" />
          </Button>
        </ModalHeader>
        <ModalBody className="space-y-3">
          {modalKind === "manufacturer" && editingManufacturer?.is_deleted ? (
            <DeletedWarningBanner />
          ) : null}
          {modalKind === "model" && editingModel?.is_deleted ? (
            <DeletedWarningBanner />
          ) : null}
          {modalKind === "manufacturer" ? (
            <VStack gap="xs">
              <FormLabel>{tCatalogTruckManufacturers("fields.name")}</FormLabel>
              <InputWrapper
                value={manufacturerForm.fields.name}
                onChange={(e) =>
                  manufacturerForm.setField("name", e.target.value)
                }
                disabled={submitting}
                data-testid="admin-truck-catalog-manufacturer-name"
              />
            </VStack>
          ) : (
            <VStack gap="xs">
              <FormLabel>{tCatalogTruckModels("fields.name")}</FormLabel>
              <InputWrapper
                value={modelForm.fields.name}
                onChange={(e) => modelForm.setField("name", e.target.value)}
                disabled={submitting}
                data-testid="admin-truck-catalog-model-name"
              />
              <Text size="xs" color="muted">
                {selectedManufacturer?.name ?? "—"}
              </Text>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <HStack align="stretch" gap="lg">
            {modalKind === "manufacturer" && editingManufacturer ? (
              <ConfirmActionButton
                isRestore={Boolean(editingManufacturer.is_deleted)}
                loading={deleteManufacturerLoading}
                disabled={submitting}
                onConfirm={() =>
                  void toggleDeleteManufacturer(
                    editingManufacturer.id,
                    Boolean(editingManufacturer.is_deleted),
                  )
                }
                className="mr-3"
              />
            ) : null}
            {modalKind === "model" && editingModel ? (
              <ConfirmActionButton
                isRestore={Boolean(editingModel.is_deleted)}
                loading={deleteModelLoading}
                disabled={submitting}
                onConfirm={() =>
                  void toggleDeleteModel(
                    editingModel.id,
                    Boolean(editingModel.is_deleted),
                  )
                }
                className="mr-3"
              />
            ) : null}
            <Button
              type="button"
              color="neutral"
              variant="outline"
              onClick={handleClose}
              disabled={
                submitting || deleteManufacturerLoading || deleteModelLoading
              }
            >
              {tCommon("cancel")}
            </Button>
            <Button
              type="button"
              loading={submitting}
              disabled={
                submitting ||
                deleteManufacturerLoading ||
                deleteModelLoading ||
                !isModalValid
              }
              onClick={() => void handleSave()}
            >
              {tCommon("save")}
            </Button>
          </HStack>
        </ModalFooter>
      </AdaptiveSheet>
      {closeConfirmDialog}
    </VStack>
  );
}

export const Route = createFileRoute("/_app/admin/catalogs/truck-catalog")({
  component: AdminTruckCatalogPage,
});
